import React, { useState, useMemo } from 'react';
import { AccountFragmentFragment, Privilege, AccountType } from '../../api';
import accountsService from '../../services/accounts';
import { useSnackbar } from 'notistack';
import styled from 'styled-components';
import { Button, Input, Checkbox, FormControl, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { RouteComponentProps, withRouter } from 'react-router';
import DeleteAccount from '../Util/DeleteAccount';
import useHasPrivilege from '../Util/useHasPrivilege';

interface Props extends RouteComponentProps {
  account: AccountFragmentFragment;
  onAddAccount: any;
}

const AccountProperties: React.FunctionComponent<Props> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [edit, setEdit] = useState(false);
  const [newAccount, setNewAccount] = useState(false);
  const [overruleId, setOverruleId] = useState(false);
  const [id, setId] = useState(props.account.id);
  const [name, setName] = useState(props.account.name);
  const [accountType, setAccountType] = useState(AccountType.Customer);
  const hasWritePermission = useHasPrivilege(Privilege.WriteUsers);

  useMemo(() => {
    setId(props.account.id);
    setName(props.account.name);
    setEdit(!props.account.id);
    setNewAccount(!props.account.id);
    setAccountType(props.account.accountType);
  }, [props.account]);

  useMemo(() => {
    if (!overruleId && newAccount && name) {
      setId(name.toLowerCase().replaceAll(/[^a-z0-9]/g, ''));
    }
  }, [name, newAccount, overruleId]);

  const onEditSave = () => {
    if (edit) {
      onSave();
    } else {
      setEdit(true);
    }
  };

  const onCancel = () => {
    setEdit(false);
   // props.history.push('/account');
  };

  const onSave = () => {
    let valid = true;
    if (id.toLowerCase().trim() === '') {
      enqueueSnackbar('You must enter a id for the account', { variant: 'info', autoHideDuration: 3000 });
      valid = false;
    }
    if (valid && newAccount) {
      accountsService.createAccount({ id, name, type: accountType }).then(
        (account: AccountFragmentFragment) => {
          setEdit(false);
          props.onAddAccount(account);
        },
        () => enqueueSnackbar('Failed to add the account', { variant: 'error', autoHideDuration: 3000 })
      );
    } else if (valid && !newAccount) {
      accountsService.updateAccount(id, { name, type: accountType, bucket: props.account.bucket }).then(
        () => setEdit(false),
        () => enqueueSnackbar('Failed to update the account', { variant: 'error', autoHideDuration: 3000 })
      );
    }
  };

  return (
    <Container>
      <Box className="sciui-card sciui-m-l sciui-m-r-m sciui-p-l sciui-bg-p-5">
        <h2>Account overview</h2>
        <Container>
          <FieldContainer>
            <Field key="name">
              <Key>Name</Key>
              <Value>
                <ValueField value={name} readOnly={!edit} onChange={setName} />
              </Value>
            </Field>
            <Field key="id">
              <Key>ID</Key>
              <Value>
                <ValueField value={id} readOnly={!overruleId || !edit || !newAccount} onChange={setId} />
              </Value>
            </Field>
            {newAccount && (
              <Field key="overruleid">
                <Key>Overrule ID</Key>
                <Value>
                  <Checkbox checked={overruleId} onChange={(e, checked) => setOverruleId(checked)} />
                </Value>
              </Field>
            )}
            {accountType === AccountType.Customer && (
              <Field key="bucket">
                <Key>Bucket</Key>
                <Value>
                  <ValueField value={props.account.bucket || ''} readOnly={true} onChange={() => {}} />
                </Value>
              </Field>
            )}
            <Field key="type">
              <Key>Type</Key>
              <Value>
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="type"
                    name="type1"
                    value={accountType}
                    onChange={(event, value) => setAccountType(value as AccountType)}>
                    <FormControlLabel
                      key="customer"
                      disabled={!edit}
                      value={AccountType.Customer}
                      control={<Radio />}
                      label="Customer"
                    />
                    <FormControlLabel
                      key="labelling"
                      disabled={!edit}
                      value={AccountType.Labelling}
                      control={<Radio />}
                      label="Labelling company"
                    />
                  </RadioGroup>
                </FormControl>
              </Value>
            </Field>
          </FieldContainer>
        </Container>
        <ButtonContainer>
          {hasWritePermission && (
            <Button onClick={onEditSave} variant="outlined" color="primary">
              {edit ? 'Save' : 'Edit'}
            </Button>
          )}
          {hasWritePermission && edit && (
            <Button onClick={onCancel} variant="outlined" color="primary">
              Cancel
            </Button>
          )}
          {hasWritePermission && !edit && (
            <DeleteAccount
              enabled={(props.account.users && props.account.users.length === 0) || !props.account.users}
              id={props.account.id}
              onDeleted={() => {}}
            />
          )}
        </ButtonContainer>
      </Box>
    </Container>
  );
};

const Box = styled.div`
  width: 50%;
  flex: 1;
  margin-top: 32px;
  margin-bottom: 32px;
`;

type ValueFieldProps = {
  readOnly: boolean;
  value: string;
  onChange: (value: string) => void;
};

const ValueField: React.FunctionComponent<ValueFieldProps> = (props: ValueFieldProps) => {
  return (
    <Input
      id="name"
      readOnly={props.readOnly}
      fullWidth
      value={props.value}
      type={typeof props.value === 'number' ? 'number' : 'text'}
      onChange={(event) => props.onChange(event.target.value as string)}
      inputProps={{
        readOnly: props.readOnly,
      }}
    />
  );
};

const FieldContainer = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Field = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Key = styled.div`
  flex-basis: 200px;
  flex-grow: 1;
  font-weight: 600;
`;

const Value = styled.div`
  flex-basis: 500px;
  flex-grow: 1;
  font-weight: 300;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 32px;
  height: 40px;
  justify-content: flex-end;
`;
export default withRouter(AccountProperties);
